// Cómo enrola el equipo cada proveedor de MDM (paso 09, crédito): qué le toca al vendedor con el
// teléfono en la mano y qué le dice la pantalla mientras tanto. Un proveedor nuevo = una fila.
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { DeviceProvisioningTypeEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceProvisioningTypeEnum';
import { MDM_REJECTION_MESSAGES } from './deviceErrors';
import type { EnrollmentView } from './deviceEnrollment';
import {
    DEVICE_ENROLLMENT_STEPS,
    MOTOSAFE_STEPS,
    TRUSTONIC_GENERIC_STEPS,
    TRUSTONIC_KNOX_GUARD_STEPS,
    TRUSTONIC_PAY_TRIGGER_STEPS,
    type DeviceEnrollmentStep,
} from './components/delivery/deviceEnrollmentSteps';
import type {
    DeviceEnrollment,
    DeviceEnrollmentFailureCode,
    DeviceEnrollmentFailureReason,
} from './types';

/**
 * Lo que el enrolamiento de un proveedor le exige a alguien parado frente al equipo. Sin ningún
 * contacto el alta es de servidor: el vendedor no toca el teléfono y el candado no es tarea suya.
 */
export type EnrollmentTouchpoint =
    /** El equipo tiene que llegar recién sacado de la caja, sin ninguna cuenta configurada. */
    | 'FACTORY_FRESH_DEVICE'
    /** Hay un instructivo que una persona ejecuta en el teléfono, con su QR y su APK. */
    | 'ON_DEVICE_STEPS';

/** Los desenlaces del enrolamiento, escritos con la verdad de cada proveedor. */
export interface EnrollmentNoticeCopy {
    confirmed: string;
    /** El enrolamiento está abierto y todavía no se resuelve. */
    waiting: { lead: string; rest: string };
    /** El proveedor ya registró el equipo, pero falta la confirmación final. */
    waitingLockSignal: { lead: string; rest: string };
    failed: { title: string; advice: string };
    providerDown: { title: string; advice: string };
}

/** El instructivo que se ejecuta en el teléfono y cuándo se muestra. */
export interface EnrollmentGuide {
    /** Pasos cuando no hay método de alta, o no hay uno específico. */
    steps: readonly DeviceEnrollmentStep[];
    stepsByProvisioning?: Partial<
        Record<DeviceProvisioningTypeEnum, readonly DeviceEnrollmentStep[]>
    >;
    /** Lleva el QR del APK del candado. */
    showsLockAppQr: boolean;
    /** Solo aparece con el IMEI ya registrado con el proveedor. */
    needsRegisteredImei: boolean;
    /** Vistas del enrolamiento en las que el instructivo se retira. */
    hiddenIn: readonly EnrollmentView[];
    /** Qué hacer si se saltó un paso en el teléfono. */
    recoveryNote?: string;
}

export interface EnrollmentFlow {
    touchpoints: readonly EnrollmentTouchpoint[];
    guide: EnrollmentGuide;
    /** Cómo se nombra el candado en el resumen del paso. `null` = no es tarea del vendedor. */
    summaryTask: string | null;
    /** Lo que el resumen aclara cuando el candado no le toca a nadie. */
    summaryNote?: string;
    /** Encabezado del bloque del candado. */
    title: string;
    desc: string;
    /** Lo que se lee antes de teclear el IMEI, cuando no hay nada que hacer en el teléfono. */
    idleNote?: string;
    copy: EnrollmentNoticeCopy;
    /** Cada cuánto repreguntar mientras el enrolamiento sigue abierto. */
    pollMs: number;
}

/** El candado lo instala una persona en el teléfono: la espera se mide en minutos, no en segundos. */
export const DEVICE_ENROLLMENT_POLL_MS = 5000;

/**
 * Datacultr: el candado es una app que alguien instala en el equipo del cliente. Este es el flujo
 * que la pantalla tuvo siempre, y el que se usa cuando no sabemos qué proveedor toca.
 */
const DATACULTR_FLOW: EnrollmentFlow = {
    touchpoints: ['FACTORY_FRESH_DEVICE', 'ON_DEVICE_STEPS'],
    guide: {
        steps: DEVICE_ENROLLMENT_STEPS,
        showsLockAppQr: true,
        needsRegisteredImei: false,
        hiddenIn: ['WAITING_LOCK_SIGNAL'],
    },
    summaryTask: 'instalar el candado en el equipo',
    title: 'Instalar el candado',
    desc: '10 pasos en el teléfono del cliente.',
    copy: {
        confirmed: 'Candado instalado y confirmado por el proveedor.',
        waiting: {
            lead: 'Esperando la confirmación del equipo.',
            rest: 'En cuanto el proveedor lo reporte registrado, esta pantalla avanza sola — no hace falta que la recargues.',
        },
        waitingLockSignal: {
            lead: 'El proveedor ya registró el equipo.',
            rest: 'Falta que el candado dé su primera señal de vida, que tarda unos minutos. Ya no hay nada que hacer en el teléfono: esta pantalla avanza sola.',
        },
        failed: {
            title: 'El enrolamiento falló.',
            advice: 'Revisa que el equipo esté recién sacado de la caja y sin ninguna cuenta configurada. Si ya tiene una, hay que restablecerlo de fábrica y repetir los pasos desde el primero.',
        },
        providerDown: {
            title: 'No podemos confirmar el enrolamiento: el proveedor no responde.',
            advice: 'No es algo que te falte en el teléfono. Si ya terminaste los pasos, el equipo puede estar registrado y nosotros todavía sin poder verlo. Seguimos consultando.',
        },
    },
    pollMs: DEVICE_ENROLLMENT_POLL_MS,
};

/**
 * Trustonic: el IMEI se sube por API y el candado se activa en la configuración inicial del teléfono.
 * No hay app ni QR; los pasos dependen del método de alta del equipo.
 */
const TRUSTONIC_FLOW: EnrollmentFlow = {
    touchpoints: ['FACTORY_FRESH_DEVICE', 'ON_DEVICE_STEPS'],
    guide: {
        steps: TRUSTONIC_GENERIC_STEPS,
        stepsByProvisioning: {
            [DeviceProvisioningTypeEnum.KNOX_GUARD]: TRUSTONIC_KNOX_GUARD_STEPS,
            [DeviceProvisioningTypeEnum.PAY_TRIGGER]: TRUSTONIC_PAY_TRIGGER_STEPS,
        },
        showsLockAppQr: false,
        needsRegisteredImei: true,
        hiddenIn: ['FAILED'],
        recoveryNote:
            'Si te saltaste una pantalla o algo falló, restablece el equipo de fábrica y repite desde el primer paso.',
    },
    summaryTask: 'activar el candado en el equipo',
    title: 'Activar el candado',
    desc: 'Se activa en el teléfono del cliente durante su configuración inicial. Sin app ni código QR.',
    copy: {
        confirmed: 'Equipo dado de alta. El proveedor ya confirmó el candado.',
        waiting: {
            lead: 'Estamos dando de alta el equipo con el proveedor.',
            rest: 'Mientras tanto, sigue los pasos en el teléfono del cliente. Esta pantalla avanza sola.',
        },
        waitingLockSignal: {
            lead: 'El proveedor ya registró el equipo.',
            rest: 'Falta que el teléfono se reporte: termina su configuración inicial con los pasos de abajo y déjalo prendido y con internet. Esta pantalla avanza sola.',
        },
        failed: {
            title: 'El alta del equipo falló.',
            advice: 'El equipo no quedó registrado con el proveedor, y no es algo que se arregle en el teléfono. Vuelve a intentarlo; si sigue fallando, repórtalo a soporte con el IMEI a la mano.',
        },
        providerDown: {
            title: 'No podemos confirmar el alta: el proveedor no responde.',
            advice: 'Si ya terminaste los pasos en el teléfono, el alta pudo haber quedado hecha y nosotros todavía sin poder verla. Seguimos consultando.',
        },
    },
    pollMs: DEVICE_ENROLLMENT_POLL_MS,
};

/**
 * Motosafe: el candado viene integrado en los Motorola. Registramos el IMEI por API y el teléfono
 * aplica el alta al sincronizarse; al vendedor solo le toca dejarlo prendido y con internet.
 */
const MOTOSAFE_FLOW: EnrollmentFlow = {
    touchpoints: ['ON_DEVICE_STEPS'],
    guide: {
        steps: MOTOSAFE_STEPS,
        showsLockAppQr: false,
        needsRegisteredImei: true,
        hiddenIn: ['FAILED'],
    },
    summaryTask: 'conectar el teléfono a internet',
    title: 'Activar el candado de Motorola',
    desc: 'Viene integrado en el teléfono: sin app ni código QR.',
    copy: {
        confirmed: 'Equipo dado de alta. Motorola ya confirmó el candado.',
        waiting: {
            lead: 'Estamos dando de alta el equipo con Motorola.',
            rest: 'Deja el teléfono prendido y con internet. Esta pantalla avanza sola.',
        },
        waitingLockSignal: {
            lead: 'Motorola ya registró el equipo.',
            rest: 'Falta que el teléfono se sincronice: déjalo prendido y con internet. Puede tardar unos minutos. Esta pantalla avanza sola.',
        },
        failed: {
            title: 'El alta del equipo con Motorola falló.',
            advice: 'El equipo no quedó registrado con Motorola, y no es algo que se arregle en el teléfono. Vuelve a intentarlo; si sigue fallando, repórtalo a soporte con el IMEI a la mano.',
        },
        providerDown: {
            title: 'No podemos confirmar el alta: Motorola no responde.',
            advice: 'Deja el teléfono prendido y con internet: el alta pudo haber quedado hecha y nosotros todavía sin poder verla. Seguimos consultando.',
        },
    },
    pollMs: DEVICE_ENROLLMENT_POLL_MS,
};

/** Qué hacer cuando el fallo no se arregla desde el wizard: depende de por qué falló. */
const FAILURE_ADVICE_BY_REASON: Record<DeviceEnrollmentFailureReason, string> = {
    MODEL_NOT_SUPPORTED:
        'El proveedor del candado no reconoce el modelo o el IMEI de este equipo. Revisa que el IMEI coincida con el de la caja y llama a soporte.',
    ALREADY_IN_PROVIDER:
        'Este equipo ya está registrado con el proveedor del candado. Llama a soporte para que lo vinculen a este crédito.',
    PROVIDER_REJECTED:
        'El proveedor del candado rechazó el registro del equipo. Llama a soporte para que lo reintenten desde el backoffice.',
};

/** Qué hacer según el code del rechazo; manda sobre el motivo cuando el silo lo manda. */
const FAILURE_ADVICE_BY_CODE: Record<DeviceEnrollmentFailureCode, string> = {
    INVALID_IMEI:
        'El proveedor del candado no acepta el IMEI de este equipo. Revisa que coincida con el de la caja y llama a soporte.',
    ...MDM_REJECTION_MESSAGES,
};

/** El aviso del fallo: título del proveedor y, si no se puede reintentar, la salida por soporte. */
export interface FailedEnrollmentNotice {
    title: string;
    advice: string;
    retryable: boolean;
}

/** Solo un `retryable: true` explícito ofrece reintentar: reenviar el POST no reabre un FAILED. */
export function failedEnrollmentNotice(
    copy: EnrollmentNoticeCopy,
    enrollment:
        | Pick<DeviceEnrollment, 'failureReason' | 'failureCode' | 'retryable'>
        | null
        | undefined,
): FailedEnrollmentNotice {
    if (enrollment?.retryable === true) return { ...copy.failed, retryable: true };
    const advice = enrollment?.failureCode
        ? FAILURE_ADVICE_BY_CODE[enrollment.failureCode]
        : FAILURE_ADVICE_BY_REASON[enrollment?.failureReason ?? 'PROVIDER_REJECTED'];
    return { title: copy.failed.title, advice, retryable: false };
}

/** La única fuente de qué enrolamiento tiene cada proveedor. */
export const ENROLLMENT_FLOWS: Record<MdmProviderEnum, EnrollmentFlow> = {
    [MdmProviderEnum.DATACULTR]: DATACULTR_FLOW,
    [MdmProviderEnum.TRUSTONIC]: TRUSTONIC_FLOW,
    [MdmProviderEnum.MOTOSAFE]: MOTOSAFE_FLOW,
};

/**
 * Sin proveedor —producto sin MDM, catálogo caído o SKU que no resuelve— se usa el flujo con
 * instructivo: callar unos pasos que sí hacían falta deja al vendedor sin salida frente al cliente.
 */
export const DEFAULT_ENROLLMENT_FLOW = DATACULTR_FLOW;

export function enrollmentFlow(provider: MdmProviderEnum | undefined): EnrollmentFlow {
    if (!provider) return DEFAULT_ENROLLMENT_FLOW;
    // Un proveedor fuera del enum no tiene flujo declarado: se lo trata como al ausente.
    const flow = ENROLLMENT_FLOWS[provider] as EnrollmentFlow | undefined;
    return flow ?? DEFAULT_ENROLLMENT_FLOW;
}

/** El candado le cuesta trabajo al vendedor, así que se numera entre sus tareas. */
export function isSellerLockTask(flow: EnrollmentFlow): boolean {
    return flow.touchpoints.length > 0;
}

/** Hay instructivo que mostrar: los pasos en el teléfono y el QR del APK que se baja ahí. */
export function showsEnrollmentGuide(flow: EnrollmentFlow): boolean {
    return flow.touchpoints.includes('ON_DEVICE_STEPS');
}

/** Los pasos del método de alta del equipo; sin método conocido, los genéricos. */
export function enrollmentGuideSteps(
    guide: EnrollmentGuide,
    provisioningType: DeviceProvisioningTypeEnum | undefined,
): readonly DeviceEnrollmentStep[] {
    return (provisioningType && guide.stepsByProvisioning?.[provisioningType]) ?? guide.steps;
}

export interface GuideVisibilityState {
    view: EnrollmentView | null;
    enrolled: boolean;
    registeredImei: string;
    /** El IMEI llegó fijado desde la firma. */
    signPinned: boolean;
}

/** Si el instructivo se ve ahora: nunca con el candado confirmado ni en las vistas que lo retiran. */
export function isGuideVisible(flow: EnrollmentFlow, state: GuideVisibilityState): boolean {
    const { guide } = flow;
    if (!showsEnrollmentGuide(flow) || state.enrolled) return false;
    if (state.view && guide.hiddenIn.includes(state.view)) return false;
    if (state.registeredImei !== '') return true;
    return !state.signPinned && !guide.needsRegisteredImei;
}

/** El aprovisionamiento no funciona sobre un equipo que ya tiene cuenta configurada. */
export function needsFactoryFreshDevice(flow: EnrollmentFlow): boolean {
    return flow.touchpoints.includes('FACTORY_FRESH_DEVICE');
}

/** Con qué número arrancan las tareas compartidas: después del IMEI (si es tarea) y del candado. */
export function sharedTasksFirstNumber(flow: EnrollmentFlow, includesImeiTask = true): number {
    return (includesImeiTask ? 2 : 1) + (isSellerLockTask(flow) ? 1 : 0);
}

const IMEI_TASK = 'registrar el IMEI';
const SHARED_TASKS: readonly string[] = [
    'instalar la app',
    'esperar su registro',
    'entregar la tarjeta',
];
const NO_SHARED_TASKS_NOTE = 'El cliente ya trae su cuenta y su tarjeta.';
const COUNT_WORDS: readonly string[] = ['Cero', 'Una', 'Dos', 'Tres', 'Cuatro', 'Cinco', 'Seis'];

/** La «y» se vuelve «e» delante de un sonido /i/: «registrar el IMEI e instalar el candado». */
function joinTasks(tasks: readonly string[]): string {
    if (tasks.length === 1) return tasks[0];
    const last = tasks[tasks.length - 1];
    const conjunction = /^h?i(?!e)/i.test(last) ? 'e' : 'y';
    return `${tasks.slice(0, -1).join(', ')} ${conjunction} ${last}`;
}

/**
 * El resumen del paso: cuántas tareas y cuáles. Se cuenta lo que de verdad le toca al vendedor —
 * anunciar una tarea que el sistema hace solo lo deja esperando algo que ya pasó.
 */
export function describeDeliveryTasks(
    flow: EnrollmentFlow,
    includesSharedTasks: boolean,
    includesImeiTask = true,
): string {
    const tasks = includesImeiTask ? [IMEI_TASK] : [];
    if (flow.summaryTask) tasks.push(flow.summaryTask);
    if (includesSharedTasks) tasks.push(...SHARED_TASKS);

    const count = COUNT_WORDS[tasks.length] ?? String(tasks.length);
    const noun = tasks.length === 1 ? 'tarea' : 'tareas';
    const notes = [
        flow.summaryTask ? undefined : flow.summaryNote,
        includesSharedTasks ? undefined : NO_SHARED_TASKS_NOTE,
    ].filter((note): note is string => !!note);

    const lead = tasks.length > 0 ? [`${count} ${noun}: ${joinTasks(tasks)}.`] : [];
    return [...lead, ...notes].join(' ');
}
